// services/bankService.ts
import axios from "axios";
import { BACKEND_URL } from "../utils/api";
import { sortByDate } from "../helpers/sortDataByDate";

export interface Transfert {
  id: string;
  user_id: string;
  sending_amount: number;
  fee: number;
  total_amount: number;
  conversion_rate: number;
  sending_country_id: number;
  received_country_id: number;
  receiving_amount: number;
  description: string;
  status: string;
  txt_reference: string;
  created_at: string;
  updated_at: string;
}

export interface BankDetails {
  id: string;
  transfert_id: string;
  wallet_id: string;
  bank_name: string;
  bank_account_owner: string;
  account_number: string;
  branch_number: string;
  security_key: string;
  status: string;
  payout_note: string | null;
  payout_processed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface BankTransfertItem {
  transfert: Transfert;
  details: BankDetails;
}

export interface PaginatedResponse {
  items: BankTransfertItem[];
  total: number;
  skip: number;
  limit: number;
}

interface PaginationParams {
  skip?: number;
  limit?: number;
  status?: string;
}

export const bankService = {
  async getTransfertsCanadaCmr(
    token: string,
    pagination?: PaginationParams,
    direction?: string
  ): Promise<BankTransfertItem[]> {
    try {
      const cleanedToken = token.replace(/^"|"$/g, "").trim();

      let url = `${BACKEND_URL}/transferts/${direction}/?skip=${pagination?.skip ?? 0}&limit=${pagination?.limit ?? 50}`;

      if (pagination?.status) {
        url += `&status=${pagination.status}`;
      }

      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${cleanedToken}`,
          accept: "application/json",
        },
      });

      const sortedTransferts = sortByDate(
        response.data?.data?.items as BankTransfertItem[],
        (item) => item.transfert.created_at,
        "desc"
      );

      return sortedTransferts || [];
    } catch (error: any) {
      console.error("Erreur getTransfertsCanadaCmr:", error.response?.data);
      throw error;
    }
  },

  // Admin: Marquer le payout comme COMPLETED
  async completePayout(token: string, extensionId: string): Promise<any> {
    const cleanedToken = token.replace(/^"|"$/g, "").trim();
    const response = await axios.post(
      `${BACKEND_URL}/transferts/canada-cmr-banque/${extensionId}/complete`,
      {},
      { headers: { Authorization: `Bearer ${cleanedToken}`, accept: "application/json" } }
    );
    return response.data;
  },

  // Admin: Marquer le payout comme IN_PROGRESS
  async inProgressPayout(token: string, extensionId: string): Promise<any> {
    const cleanedToken = token.replace(/^"|"$/g, "").trim();
    const response = await axios.post(
      `${BACKEND_URL}/transferts/canada-cmr-banque/${extensionId}/in-progress`,
      {},
      { headers: { Authorization: `Bearer ${cleanedToken}`, accept: "application/json" } }
    );
    return response.data;
  },

  // Admin: Marquer le payout comme FAILED
  async failPayout(token: string, extensionId: string): Promise<any> {
    const cleanedToken = token.replace(/^"|"$/g, "").trim();
    const response = await axios.post(
      `${BACKEND_URL}/transferts/canada-cmr-banque/${extensionId}/fail`,
      {},
      { headers: { Authorization: `Bearer ${cleanedToken}`, accept: "application/json" } }
    );
    return response.data;
  },

  // Admin: Marquer le payout comme CANCELED
  async cancelPayout(token: string, extensionId: string): Promise<any> {
    const cleanedToken = token.replace(/^"|"$/g, "").trim();
    const response = await axios.post(
      `${BACKEND_URL}/transferts/canada-cmr-banque/${extensionId}/cancel`,
      {},
      { headers: { Authorization: `Bearer ${cleanedToken}`, accept: "application/json" } }
    );
    return response.data;
  },

  // Vérifier la clé de sécurité
  async verifySecurityKey(
    token: string,
    transfertId: string,
    data: { security_key: string; account_number?: string; phone_number?: string }
  ): Promise<any> {
    const cleanedToken = token.replace(/^"|"$/g, "").trim();
    const response = await axios.post(`${BACKEND_URL}/transferts/canada-cmr-banque/${transfertId}/verify-key`, data, {
      headers: { Authorization: `Bearer ${cleanedToken}`, accept: "application/json" },
    });
    return response.data;
  },
};
